'use client';

import { useEarnings } from '@/api/queries/earnings.queries';
import { Avatar } from '@/components/ui/avatar';
import { useFeature } from '@/hooks/use-feature';
import { Skeleton } from '@/components/ui/skeleton';
import { TabooIsotype } from '@/components/ui/taboo-isotype';
import { isPrivateCreator } from '@/shared/lib/auth/permissions';
import { formatCurrency } from '@/shared/utils/formatting';
import { getChannelHandle, getCreatorRoute } from '@/shared/utils/routes';
import { getUserDisplay } from '@/shared/utils/user-display';
import type { User } from '@/types';
import { ChevronRight, DollarSign, User as UserIcon } from 'lucide-react';
import Link from 'next/link';
import { useChromePathname } from '@/shared/components/providers/chrome-pathname';

// Same query key as the /studio pages, so opening the menu warms the studio cache.
// Errors (incl. NO_PROMOTER_ACCOUNT, an expected state) render no amount; /studio
// owns reporting real earnings failures.
function useCurrentBalance() {
  const { data, isLoading } = useEarnings('30d', 'day');
  return { current: data?.balance?.current, isLoading };
}

function StudioBalance() {
  const { current, isLoading } = useCurrentBalance();

  if (isLoading) return <Skeleton className="h-4 w-16 shrink-0" />;
  if (current === undefined) return null;

  return (
    <span className="shrink-0 font-semibold text-green-500 tabular-nums">
      <span className="sr-only">Balance </span>
      {formatCurrency(current)}
    </span>
  );
}

// Inside /studio the Creator Studio row becomes the "Taboo TV" exit link, so the
// balance moves to its own block rather than reading as the exit link's amount.
function TotalBalanceBlock({ onClose }: { onClose?: () => void }) {
  const { current, isLoading } = useCurrentBalance();
  const payoutsEnabled = useFeature('STUDIO_PAYOUTS');

  if (!isLoading && current === undefined) return null;

  return (
    <>
      <Link
        href={payoutsEnabled ? '/studio/payouts' : '/studio'}
        {...(onClose !== undefined ? { onClick: onClose } : {})}
        className="mx-2 my-1 flex items-center gap-3 rounded px-2 py-2 hover:bg-white/10 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <p className="text-xs text-white/50">Total balance</p>
          {current === undefined ? (
            <Skeleton className="mt-1 h-7 w-32" />
          ) : (
            <p className="text-2xl font-bold text-green-500 tabular-nums">
              {formatCurrency(current)}
            </p>
          )}
        </div>
        <ChevronRight className="w-4 h-4 shrink-0 text-white/40" />
      </Link>

      <div className="h-px bg-white/10 -mx-1 my-1" />
    </>
  );
}

export function CreatorIdentity({ user, onClose }: { user: User; onClose?: () => void }) {
  const pathname = useChromePathname();
  const isInStudio = pathname?.startsWith('/studio') ?? false;

  const displayName = getUserDisplay(user);
  const handler = user.handler || getChannelHandle(user.channel);
  const email = user.email;

  const showCorrectProfile = () => {
    const isPrivate = isPrivateCreator(user);

    const href = isPrivate ? '/profile' : getCreatorRoute(handler);
    const clickHandler = onClose ? { onClick: onClose } : {};

    return (
      <Link
        href={href}
        {...clickHandler}
        className="mx-2 my-1 flex items-center gap-3 rounded px-2 py-2 text-sm hover:bg-white/10 transition-colors"
      >
        {isPrivate ? (
          <>
            <UserIcon className="w-4 h-4" />
            My Profile
          </>
        ) : (
          <>
            <span className="flex items-center justify-center w-8 h-8 rounded-full border-[1.5px] border-red-primary">
              <TabooIsotype size={16} className="text-red-primary" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white">My Channel</p>
              <p className="text-xs text-white/50 truncate">@{handler}</p>
            </div>
          </>
        )}
      </Link>
    );
  };

  return (
    <>
      <div className="px-3 py-3 flex items-center gap-3">
        <Avatar src={user.dp} alt={displayName} size="md" fallback={displayName} />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-white truncate">{displayName}</p>
          <p className="text-xs text-white/50 truncate">{email}</p>
        </div>
      </div>

      <div className="h-px bg-white/10 -mx-1 my-1" />

      {handler && (
        <>
          {isInStudio && <TotalBalanceBlock {...(onClose !== undefined ? { onClose } : {})} />}

          {showCorrectProfile()}

          <Link
            href={isInStudio ? '/' : '/studio'}
            {...(onClose !== undefined ? { onClick: onClose } : {})}
            className="mx-2 flex items-center gap-3 rounded px-2 py-2 text-sm hover:bg-white/10 transition-colors"
          >
            <DollarSign className="w-4 h-4 shrink-0 text-red-primary" />
            <span className="min-w-0 flex-1 truncate">
              {isInStudio ? 'Taboo TV' : 'Creator Studio'}
            </span>
            {!isInStudio && (
              <>
                <StudioBalance />
                <ChevronRight className="w-4 h-4 shrink-0 text-white/40" />
              </>
            )}
          </Link>

          <div className="h-px bg-white/10 -mx-1 my-1" />
        </>
      )}
    </>
  );
}
